/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2017, 2022
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { Button, TextInput } from 'carbon-components-react';
import Invoker from '../utils/invoker';
import { getOauthErrorMessage, getMixedContentMessage } from '../utils/error-message-handler';
import isEqual from 'lodash/isEqual';
import { View16, ViewOff16 } from '@carbon/icons-react';

export class TestToolAccessToken extends Component {
  constructor(props) {
    super(props);
    this.invoker = new Invoker();
    this.getToken = this.getToken.bind(this);
    this.updateToken = this.updateToken.bind(this);
    this.state = {
      authToken: {
        access_token: ''
      },
      showPassword: false
    };
  }

  componentDidMount() {
    this.configure(this.props);
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps, this.props)) {
      this.configure(this.props);
    }
  }

  configure(props) {
    if (!this.state.authToken['access_token']) {
      let initialToken = {};
      initialToken['access_token'] = '';
      this.setState({ authToken: initialToken });
    }
    if (props.config) {
      let newState = Object.assign(this.state, props.config);
      this.setState(newState);
    }
  }

  async getToken() {
    let { t } = this.props;
    let newState = {
      authToken: {
        access_token: ''
      }
    };

    // mixed content check - i.e. calling http from https - not allowed.
    if (
      window.location.protocol === 'https:' &&
      this.state.flags.selectedTokenUrl &&
      this.state.flags.selectedTokenUrl.lastIndexOf(window.location.protocol) !== 0
    ) {
      newState.authError = getMixedContentMessage(t);
      this.setState(newState);
      return;
    }

    if (this.props.config.flags.oauthFlow === 'implicit') {
      // authorize calls call window.open, we can't get a handle on the response so just call it.
      this.invoker.authorize(
        this.props.config.flags,
        this.props.config.identification.clientID,
        this.props.config.scopes,
        this.props.config.identification.redirectUri
      );
    } else {
      try {
        let response = await this.invoker.getToken(
          this.props.config.flags,
          this.props.config.authToken,
          this.props.config.identification.clientID,
          this.props.config.identification.clientSecret,
          this.props.config.scopes,
          this.props.config.identification.redirectUri,
          this.props.config.userCredentials.username,
          this.props.config.userCredentials.passwd
        );

        Object.keys(response).forEach((key) => {
          newState.authToken[key] = response[key];
          newState.authError = {};
        });
      } catch (err) {
        newState.authError = await getOauthErrorMessage(err, t);
      } finally {
        this.setState(newState);
        if (this.props.onChange && typeof this.props.onChange === 'function') {
          this.props.onChange(newState.authToken);
        }
      }
    }
  }

  updateToken(event) {
    let newToken = {};
    newToken['access_token'] = event.target.value;
    this.setState({ authToken: newToken });

    if (this.props.onChange && typeof this.props.onChange === 'function') {
      this.props.onChange(newToken);
    }
  }

  render() {
    let { t, config } = this.props;

    let { authToken, authError } = this.state;

    let helpText = '';
    if (config.removeFields) {
      helpText =
        config.flags.oauthFlow === 'accessCode' || config.flags.oauthFlow === 'authorizationCode'
          ? t('explorer_help_access_code')
          : t('explorer_help_implicit');
    }

    return (
      <div className="getToken">
        {!config.removeFields && (
          <Button className="getTokenButton" onClick={this.getToken}>
            {t('explorer_get_token')}
          </Button>
        )}
        <TextInput
          className="tokenField"
          labelText={t('explorer_access_token')}
          type={this.state.showPassword ? 'text' : 'password'}
          id="tokenField"
          value={authToken['access_token']}
          onChange={this.updateToken}
          helperText={helpText}
        />
        <Button
          hasIconOnly
          className="togglePassword"
          kind="ghost"
          iconDescription={this.state.showPassword ? 'Hide access token' : 'Show access token'}
          renderIcon={this.state.showPassword ? ViewOff16 : View16}
          onClick={() => {
            this.setState({ showPassword: !this.state.showPassword });
          }}
        />
        {authError && authError.highlightedText && (
          <div className="authError">
            <pre>
              <code className="boundedSection hljs oauthError" dangerouslySetInnerHTML={authError.highlightedText} />
            </pre>
          </div>
        )}
      </div>
    );
  }
}

export default withTranslation()(TestToolAccessToken);
